import { alpha } from "@mui/material/styles";

/**
 * Design tokens — single source of truth for all repeated visual values.
 * Shared between packages/ui and packages/landing.
 *
 * See .agent/skills/ui-design-system/SKILL.md for full design principles.
 */

/** Alpha opacity values — standardized fractions for consistent transparency */
export const ALPHA = {
  TABLE_HEADER: 0.03,       // Table head tinted background
  ROW_HOVER: 0.02,          // Table row hover
  SURFACE_TINT: 0.05,       // Subtle section background tint
  STAT_TILE_BG: 0.08,       // Stat tile / icon-box background
  STAT_TILE_BORDER: 0.18,   // Stat tile border
  NAV_ACTIVE_BG: 0.08,      // Active nav item background
  NAV_ACTIVE_HOVER: 0.12,   // Active nav item hover
  FOCUS_RING: 0.2,          // Input / button focus ring
  FEATURE_CARD_TINT: 0.03,  // Subtle background tint on feature/highlight cards
  DIVIDER_ITEM: 0.04,       // Hairline separator between list items
  DIVIDER_SECTION: 0.06,    // Hairline separator between sections/headers
  HOVER_DEFAULT: 0.04,      // Default hover state for interactive elements
  HOVER_ACTIVE: 0.08,       // Hover state for selected/active elements
} as const;

/** Border width for the top-accent on feature/highlight cards (px) */
export const FEATURE_CARD_ACCENT_WIDTH = 4;

/** Focus ring design tokens — consistent keyboard focus across all interactive elements */
export const FOCUS_RING = {
  OFFSET: 2,      // px — gap between element and ring
  WIDTH: 2,       // px — ring thickness
  TRANSITION: "150ms ease", // transition duration for focus ring
} as const;

/** Icon sizes (px) — consistent across all components */
export const ICON = {
  XS: 14,    // Inline text icons
  SM: 16,    // Compact / dense UI
  MD: 18,    // Section header icons
  LG: 20,    // Standard inline icons
  XL: 24,    // Default MUI "medium"
  XXL: 32,   // Feature icons in cards
  EMPTY: 40, // Empty state illustrations
} as const;

/**
 * Surface shadows — use these factory functions instead of arbitrary shadow strings.
 * primaryMain = theme.palette.primary.main
 * black       = theme.palette.common.black
 */
export const getShadow = {
  sm: (primaryMain: string) => `0 2px 8px ${alpha(primaryMain, 0.05)}`,
  md: (primaryMain: string) => `0 4px 20px ${alpha(primaryMain, 0.08)}`,
  lg: (black: string) => `0 8px 32px ${alpha(black, 0.1)}`,
} as const;
